import type { MHovState } from "../types";
import {
  compareHuge,
  huge,
  hugeOne,
  hugeZero,
  minHuge,
  type HugeNumber,
} from "@/shared/numbers";
import { NUMBER_LIMIT_BOUNDARY } from "@/features/hoverIdle/core/numberLimitBoundary";
import { resolveFreshnessStalingExponent } from "@/features/hoverIdle/wall/freshnessWallLogic";
import type { MHovBalance } from "@/features/hoverIdle/balance/hovResetBalanceTypes";

export const MHOV_UNLOCK_THRESHOLD = 100_000;
export const MHOV_EXECUTE_THRESHOLD = MHOV_UNLOCK_THRESHOLD;
export const MHOV_REWARD_REFERENCE_HOV = huge(100_000);
export const MHOV_REWARD_REFERENCE_MULTIPLIER = 10;
export const MHOV_REWARD_GROWTH_SCALE = 11;
export const MHOV_REWARD_EXPONENT = 0.59;
export const MHOV_REWARD_MULTIPLIER_SOFTCAP = NUMBER_LIMIT_BOUNDARY;
export const MHOV_REWARD_MULTIPLIER_SOFTCAP_SLOPE = 0.4;

// Hold-to-execute duration per speed setting.
export const MHOV_HOLD_MS_NORMAL = 5000;
export const MHOV_HOLD_MS_FAST = 2000;
export const MHOV_HOLD_MS_INSTANT = 0;

// Animation duration per speed setting.
export const MHOV_ANIMATION_MS_NORMAL = 5000;
export const MHOV_ANIMATION_MS_FAST = 2000;

export function createDefaultMHovState(): MHovState {
  return {
    count: 0,
    hgMultiplier: hugeOne(),
    currentLoopHovEarned: hugeZero(),
    maxHovSinceLastMHov: hugeZero(),
    lastRewardBaseHov: hugeZero(),
  };
}

export interface MHovUnlockInputs {
  readonly currentLoopHovEarned: HugeNumber;
}

// 一度でもMHovが解除されたかどうかの判定に利用。主に設定やヘルプ表示に影響。
export function isMHovUnlocked(
  inputs: MHovUnlockInputs,
  balance?: MHovBalance,
): boolean {
  return (
    compareHuge(
      inputs.currentLoopHovEarned,
      balance?.executeThreshold ?? MHOV_UNLOCK_THRESHOLD,
    ) >= 0
  );
}

// MHov 表示・実行可否の判定入力。
// `currentLoopHovEarned` は現在 run の実行 threshold 判定に使う。
// sticky な MHov unlock（`MHovSnapshot.isUnlocked`）はボタン本体の
// 表示・実行可否には参加せず、help / nav / settings の表示補助に限定する。
// `freshRewardMultiplier` は鮮度弱化前の preview、`effectiveRewardMultiplier`
// は実行時に実際に適用される値。鮮度の壁外では両者が一致する。
export interface MHovButtonStateInputs {
  readonly currentLoopHovEarned: HugeNumber;
  readonly currentMultiplier: HugeNumber;
  readonly freshRewardMultiplier: HugeNumber;
  readonly effectiveRewardMultiplier: HugeNumber;
}

function meetsMHovExecuteThreshold(
  currentLoopHovEarned: HugeNumber,
  balance?: MHovBalance,
): boolean {
  return (
    compareHuge(
      currentLoopHovEarned,
      balance?.executeThreshold ?? MHOV_EXECUTE_THRESHOLD,
    ) >= 0
  );
}

// 鮮度弱化前の preview が現在値を超える間、MHov ボタンを表示する。
// 鮮度の壁中は effective が現在値以下でも表示は維持する。
// 現在 run の実行 threshold を満たさない限り、ボタンは表示しない。
export function shouldShowMHovButton(
  inputs: MHovButtonStateInputs,
  balance?: MHovBalance,
): boolean {
  if (!meetsMHovExecuteThreshold(inputs.currentLoopHovEarned, balance)) {
    return false;
  }
  return (
    compareHuge(inputs.freshRewardMultiplier, inputs.currentMultiplier) > 0
  );
}

// 実際に受け取る effective が現在値を超える時のみ実行可能。
// 鮮度弱化中の見かけ上の preview だけでは実行を許可しない。
// 現在 run の実行 threshold を満たさない限り、実行は許可しない。
export function canExecuteMHov(
  inputs: MHovButtonStateInputs,
  balance?: MHovBalance,
): boolean {
  if (!meetsMHovExecuteThreshold(inputs.currentLoopHovEarned, balance)) {
    return false;
  }
  return (
    compareHuge(inputs.effectiveRewardMultiplier, inputs.currentMultiplier) > 0
  );
}

export function computeRawMHovRewardMultiplier(
  rewardBaseHov: HugeNumber,
  exponent?: number,
  balance?: MHovBalance,
): number {
  const reward = computeRawMHovRewardMultiplierHuge(
    rewardBaseHov,
    exponent,
    balance,
  ).toNumber();
  if (!Number.isFinite(reward)) {
    return Number.MAX_VALUE;
  }

  return Math.max(1, reward);
}

// HugeNumber-preserving variant so saturated-number comparisons cannot lose
// auto-execution gating once the reward multiplier exceeds Number.MAX_VALUE.
export function computeRawMHovRewardMultiplierHuge(
  rewardBaseHov: HugeNumber,
  exponent?: number,
  balance?: MHovBalance,
): HugeNumber {
  if (compareHuge(rewardBaseHov, 0) <= 0) return huge(1);
  const resolvedExponent =
    exponent ?? balance?.rewardExponent ?? MHOV_REWARD_EXPONENT;
  const reward = rewardBaseHov
    .div(balance?.rewardReferenceHov ?? MHOV_REWARD_REFERENCE_HOV)
    .pow(resolvedExponent)
    .sub(1)
    .mul(balance?.rewardGrowthScale ?? MHOV_REWARD_GROWTH_SCALE)
    .add(
      balance?.rewardReferenceMultiplier ?? MHOV_REWARD_REFERENCE_MULTIPLIER,
    );
  return compareHuge(reward, 1) < 0 ? huge(1) : reward;
}

export function applyMHovRewardMultiplierSoftcap(
  rawRewardMultiplier: HugeNumber,
  balance?: MHovBalance,
): HugeNumber {
  if (compareHuge(rawRewardMultiplier, MHOV_REWARD_MULTIPLIER_SOFTCAP) <= 0) {
    return rawRewardMultiplier;
  }

  const capLog10 = MHOV_REWARD_MULTIPLIER_SOFTCAP.log10();
  const effectiveLog10 = capLog10.add(
    rawRewardMultiplier
      .log10()
      .sub(capLog10)
      .mul(balance?.rewardSoftcapSlope ?? MHOV_REWARD_MULTIPLIER_SOFTCAP_SLOPE),
  );
  return huge(10).pow(effectiveLog10);
}

// Ratio-space interpolation between the player's current HG multiplier and
// the fresh MHov reward multiplier, used while the Freshness Wall is active.
// Slides the staling exponent from 1 (fresh) to FRESHNESS_WALL_STALE_FLOOR_EXPONENT
// (stale) as `elapsedSinceHovRunMs` reaches FRESHNESS_WALL_STALE_DURATION_MS.
export interface MHovFreshnessStalingInputs {
  readonly currentMultiplier: number;
  readonly freshRewardMultiplier: number;
  readonly elapsedSinceHovRunMs: number;
}

export function applyMHovFreshnessStaling(
  inputs: MHovFreshnessStalingInputs,
): number {
  const { currentMultiplier, freshRewardMultiplier, elapsedSinceHovRunMs } =
    inputs;
  if (!Number.isFinite(currentMultiplier) || currentMultiplier <= 0) {
    return freshRewardMultiplier;
  }
  if (!Number.isFinite(freshRewardMultiplier) || freshRewardMultiplier <= 0) {
    return freshRewardMultiplier;
  }
  const exponent = resolveFreshnessStalingExponent(elapsedSinceHovRunMs);
  if (exponent >= 1) return freshRewardMultiplier;
  const freshRatio = freshRewardMultiplier / currentMultiplier;
  if (!Number.isFinite(freshRatio) || freshRatio <= 0) {
    return freshRewardMultiplier;
  }
  const effective = currentMultiplier * freshRatio ** exponent;
  if (!Number.isFinite(effective)) return Number.MAX_VALUE;
  return Math.max(1, effective);
}

export interface MHovFreshnessStalingHugeInputs {
  readonly currentMultiplier: HugeNumber;
  readonly freshRewardMultiplier: HugeNumber;
  readonly elapsedSinceHovRunMs: number;
}

export function applyMHovFreshnessStalingHuge(
  inputs: MHovFreshnessStalingHugeInputs,
): HugeNumber {
  const { currentMultiplier, freshRewardMultiplier, elapsedSinceHovRunMs } =
    inputs;
  if (compareHuge(currentMultiplier, 0) <= 0) return freshRewardMultiplier;
  if (compareHuge(freshRewardMultiplier, 0) <= 0) return freshRewardMultiplier;
  const exponent = resolveFreshnessStalingExponent(elapsedSinceHovRunMs);
  if (exponent >= 1) return freshRewardMultiplier;
  const freshRatio = freshRewardMultiplier.div(currentMultiplier);
  if (compareHuge(freshRatio, 0) <= 0) return freshRewardMultiplier;
  const effective = currentMultiplier.mul(freshRatio.pow(exponent));
  return compareHuge(effective, 1) < 0 ? huge(1) : effective;
}

export interface ResolveMHovRewardMultiplierOptions {
  readonly exponent?: number;
  readonly balance?: MHovBalance;
  // `outside-mhov` lifts the pre-existing hard cap, but the post-boundary
  // softcap still applies.
  readonly hardCapLifted?: boolean;
  readonly freshnessStaling?: {
    readonly currentMultiplier: HugeNumber;
    readonly elapsedSinceHovRunMs: number;
  };
}

export interface MHovRewardMultiplierResolution {
  readonly rawRewardMultiplier: HugeNumber;
  readonly effectiveFreshRewardMultiplier: HugeNumber;
  readonly effectiveRewardMultiplier: HugeNumber;
}

export function resolveMHovRewardMultiplier(
  rewardBaseHov: HugeNumber,
  options: ResolveMHovRewardMultiplierOptions = {},
): MHovRewardMultiplierResolution {
  const rawRewardMultiplier = computeRawMHovRewardMultiplierHuge(
    rewardBaseHov,
    options.exponent,
    options.balance,
  );
  const hardCapLimitedRewardMultiplier =
    options.hardCapLifted === true
      ? rawRewardMultiplier
      : minHuge(rawRewardMultiplier, MHOV_REWARD_MULTIPLIER_SOFTCAP);
  const effectiveFreshRewardMultiplier = applyMHovRewardMultiplierSoftcap(
    hardCapLimitedRewardMultiplier,
    options.balance,
  );
  const effectiveRewardMultiplier =
    options.freshnessStaling !== undefined
      ? applyMHovFreshnessStalingHuge({
          currentMultiplier: options.freshnessStaling.currentMultiplier,
          freshRewardMultiplier: effectiveFreshRewardMultiplier,
          elapsedSinceHovRunMs: options.freshnessStaling.elapsedSinceHovRunMs,
        })
      : effectiveFreshRewardMultiplier;

  return {
    rawRewardMultiplier,
    effectiveFreshRewardMultiplier,
    effectiveRewardMultiplier,
  };
}

// Inverse of the Freshness-Wall-inactive effective reward multiplier: the
// smallest `runBaseHov` whose `effectiveFreshRewardMultiplier` reaches
// `targetMultiplier`. Mirrors the forward chain (raw power law -> hard cap ->
// softcap) so a crossing predictor stays consistent with the auto-execute gate.
// Returns null when the target is unreachable (softcap ceiling without
// `hardCapLifted`) or the inputs are non-finite so callers fall back to search.
// Freshness staling is intentionally excluded; it is non-monotone in elapsed
// time and must keep using the existing search.
export function resolveMinRunBaseHovForMHovRewardMultiplier(
  targetMultiplier: HugeNumber,
  options: {
    readonly exponent?: number;
    readonly hardCapLifted?: boolean;
    readonly balance?: MHovBalance;
  } = {},
): HugeNumber | null {
  if (!targetMultiplier.isFinite()) return null;
  const balance = options.balance;
  const exponent =
    options.exponent ?? balance?.rewardExponent ?? MHOV_REWARD_EXPONENT;
  if (!Number.isFinite(exponent) || exponent <= 0) return null;

  // The effective reward multiplier never drops below the reward at the execute
  // threshold, so any target at or below the reference reward is met there.
  const rewardReferenceMultiplier =
    balance?.rewardReferenceMultiplier ?? MHOV_REWARD_REFERENCE_MULTIPLIER;
  const rewardReferenceHov =
    balance?.rewardReferenceHov ?? MHOV_REWARD_REFERENCE_HOV;
  if (compareHuge(targetMultiplier, rewardReferenceMultiplier) <= 0) {
    return huge(rewardReferenceHov);
  }

  let requiredRaw: HugeNumber;
  if (compareHuge(targetMultiplier, MHOV_REWARD_MULTIPLIER_SOFTCAP) <= 0) {
    requiredRaw = targetMultiplier;
  } else if (options.hardCapLifted === true) {
    // Invert the log-space softcap: effLog10 = capLog10 + (rawLog10 - capLog10)*slope.
    const capLog10 = MHOV_REWARD_MULTIPLIER_SOFTCAP.log10();
    const rawLog10 = capLog10.add(
      targetMultiplier
        .log10()
        .sub(capLog10)
        .div(
          balance?.rewardSoftcapSlope ?? MHOV_REWARD_MULTIPLIER_SOFTCAP_SLOPE,
        ),
    );
    requiredRaw = huge(10).pow(rawLog10);
  } else {
    // Hard cap keeps effective reward at the softcap; the target is unreachable.
    return null;
  }

  // Invert raw = ((rb/REF)^e - 1)*SCALE + REFMULT.
  const ratioBase = requiredRaw
    .sub(rewardReferenceMultiplier)
    .div(balance?.rewardGrowthScale ?? MHOV_REWARD_GROWTH_SCALE)
    .add(1);
  if (!ratioBase.isFinite() || compareHuge(ratioBase, 0) <= 0) {
    return huge(rewardReferenceHov);
  }
  const runBaseHov = huge(rewardReferenceHov).mul(ratioBase.pow(1 / exponent));
  if (!runBaseHov.isFinite()) return null;
  return compareHuge(runBaseHov, rewardReferenceHov) < 0
    ? huge(rewardReferenceHov)
    : runBaseHov;
}

export interface MHovExecuteOutcome {
  readonly newCount: number;
  readonly newHgMultiplier: HugeNumber;
  readonly rewardMultiplier: HugeNumber;
  // Used by `outside-mhov` detection so the unlock remains based on the raw
  // formula even while the applied reward is hard-capped or softcapped.
  readonly rawRewardMultiplier: HugeNumber;
}

export type MHovExecuteOutcomeOptions = ResolveMHovRewardMultiplierOptions;

export function computeMHovExecuteOutcome(
  state: MHovState,
  rewardBaseHov: HugeNumber,
  options: MHovExecuteOutcomeOptions = {},
): MHovExecuteOutcome {
  const resolution = resolveMHovRewardMultiplier(rewardBaseHov, options);
  return {
    newCount: state.count + 1,
    newHgMultiplier: resolution.effectiveRewardMultiplier,
    rewardMultiplier: resolution.effectiveRewardMultiplier,
    rawRewardMultiplier: resolution.rawRewardMultiplier,
  };
}
